import * as React from "react";
import { PanelContext } from "./PanelContext";
import { PanelHeader } from "./PanelHeader";
import { PanelContent } from "./PanelContent";
import { PanelMobile } from "./PanelMobile";
import {
  cn,
  usePanelShell,
  getPanelContainerStyle,
  getPanelInnerStyle,
} from "./PanelShared";
import type { TypePanelProps } from "./PanelTypes";

/**
 * Tailwind/CSS-class shell — the preferred path, routed to by the hybrid when
 * the consumer passes no styled-system props or `styled()` extension. Renders a
 * plain `<aside>` (implicit `complementary` role) with the `seeds-panel` classes
 * from panel.css instead of a styled container. Runtime geometry (flex-basis,
 * gap margin, direction shape, inner min-width/min-height) is applied inline
 * because the jsdom tests assert those as computed styles and panel.css is not
 * loaded there. The mobile path renders identically to the styled shell via
 * the shared `PanelMobile`.
 */
export const PanelTailwind = (props: TypePanelProps) => {
  const shell = usePanelShell(props);
  const {
    rest,
    header,
    headerless,
    footer,
    title,
    titleId,
    direction,
    width,
    gap,
    id,
    ariaLabel,
    isPanelOpen,
    isMobile,
    enrichedValue,
    renderedChildren,
    effectiveAriaLabelledBy,
  } = shell;

  if (isMobile) {
    return <PanelMobile shell={shell} />;
  }

  // `rest` carries only non-styled pass-through props on this path (the gate
  // routes styled-system props to PanelStyled). Peel off className/style so we
  // can merge them onto the component's own class and inline geometry rather
  // than letting a spread clobber them.
  const { className, style, ...domRest } = rest as {
    className?: string;
    style?: React.CSSProperties;
  } & Record<string, unknown>;

  const containerStyle = getPanelContainerStyle(
    direction,
    width,
    gap,
    isPanelOpen
  );
  const innerStyle = getPanelInnerStyle(direction, width);

  return (
    <PanelContext.Provider value={enrichedValue}>
      <aside
        {...domRest}
        className={cn("seeds-panel", className)}
        style={{ ...containerStyle, ...style }}
        data-qa-panel={id}
        data-qa-panel-isopen={isPanelOpen}
        aria-labelledby={effectiveAriaLabelledBy}
        aria-label={ariaLabel}
      >
        <div className="seeds-panel-inner" style={innerStyle}>
          {header ??
            (headerless ? null : <PanelHeader title={title} id={titleId} />)}
          <PanelContent>{renderedChildren}</PanelContent>
          {footer}
        </div>
      </aside>
    </PanelContext.Provider>
  );
};
